import { WindowSize, Point, EmojiGameLevel, EmojiTheme, EmojiGameConfig, CapturedEmoji } from '../utils/types';
import { gameService } from './gameService';
import { emojiRescueService } from './emojiRescueService';
import { getThemeForLevel } from '../utils/emojiThemes';

export class LevelService {
  private static readonly LEVELS_BASE_CONFIG = {
    pathDuration: 5000, // Base duration for each emoji's path
    baseTapsPerEmoji: 2, // Taps allowed per emoji
    extraTapsPerLevel: 5, // Extra taps per level
    maxEmojiCount: 10, // Max emojis per level
    minIntervalBetweenEmojis: 500, // Min time between emojis (ms)
    maxIntervalBetweenEmojis: 3000, // Max time between emojis at level 1 (ms)
    intervalDecreasePerLevel: 100 // How much interval decreases per level (ms)
  };

  static getLevelConfig(level: number): EmojiGameLevel {
    console.log(`🎯 [LevelService] getLevelConfig called with level: ${level}`);
    
    const theme = getThemeForLevel(level);
    
    // Calculate concurrent emojis (max 10 for performance)
    const maxConcurrentEmojis = Math.min(
      this.LEVELS_BASE_CONFIG.maxEmojiCount,
      Math.max(1, level) // e.g., L1: 1, L2: 2, L3: 3, etc., max 10 emojis
    );
    
    // Calculate total emojis for level (can be more than 10 for higher levels)
    const totalEmojisForLevel = Math.max(1, level); // L1: 1, L2: 2, ..., L15: 15, etc.
    
    console.log(`[LevelService] Level ${level}: ${maxConcurrentEmojis} concurrent emojis (max on screen), ${totalEmojisForLevel} total emojis to spawn`);

    const intervalBetweenEmojis = Math.max(
      this.LEVELS_BASE_CONFIG.minIntervalBetweenEmojis,
      this.LEVELS_BASE_CONFIG.maxIntervalBetweenEmojis - (level - 1) * this.LEVELS_BASE_CONFIG.intervalDecreasePerLevel
    );

    // Base taps calculation on total emojis, not just concurrent ones
    const maxTaps = (totalEmojisForLevel * this.LEVELS_BASE_CONFIG.baseTapsPerEmoji) + this.LEVELS_BASE_CONFIG.extraTapsPerLevel;

    return {
      level,
      beeCount: maxConcurrentEmojis, // Keep for backward compatibility
      intervalBetweenBees: intervalBetweenEmojis, // Keep for backward compatibility
      maxTaps,
      pathDuration: this.LEVELS_BASE_CONFIG.pathDuration,
      theme,
      emojiCount: maxConcurrentEmojis, // For initial spawning
      intervalBetweenEmojis,
      totalEmojisForLevel,
      maxConcurrentEmojis
    };
  }

  static getEmojiLevelConfig(level: number): EmojiGameLevel {
    return this.getLevelConfig(level); // Delegate to the main method
  }

  static async generateLevelPaths(levelConfig: EmojiGameLevel, windowSize: WindowSize): Promise<Point[][]> {
    const paths: Point[][] = [];
    
    // Generate paths for each emoji
    for (let i = 0; i < levelConfig.emojiCount; i++) {
      const path = await gameService.generatePath(windowSize);
      paths.push(path);
    }

    return paths;
  }

  static async generateEmojiLevelPaths(levelConfig: EmojiGameLevel, windowSize: WindowSize): Promise<Point[][]> {
    const paths: Point[][] = [];
    
    // Generate paths for each emoji
    for (let i = 0; i < levelConfig.emojiCount; i++) {
      const path = await gameService.generatePath(windowSize);
      paths.push(path);
    }

    return paths;
  }

  static calculateLevelConfig(levelConfig: EmojiGameLevel): EmojiGameConfig {
    return {
      totalDuration: levelConfig.pathDuration + (levelConfig.emojiCount - 1) * levelConfig.intervalBetweenEmojis,
      beeCount: levelConfig.emojiCount, // Keep for backward compatibility
      emojiCount: levelConfig.emojiCount,
      maxTaps: levelConfig.maxTaps,
      theme: levelConfig.theme,
      totalEmojisForLevel: levelConfig.totalEmojisForLevel,
      maxConcurrentEmojis: levelConfig.maxConcurrentEmojis
    };
  }

  static calculateEmojiLevelConfig(levelConfig: EmojiGameLevel): EmojiGameConfig {
    return {
      totalDuration: levelConfig.pathDuration + (levelConfig.emojiCount - 1) * levelConfig.intervalBetweenEmojis,
      beeCount: levelConfig.emojiCount, // Keep for backward compatibility
      emojiCount: levelConfig.emojiCount,
      maxTaps: levelConfig.maxTaps,
      theme: levelConfig.theme,
      totalEmojisForLevel: levelConfig.totalEmojisForLevel,
      maxConcurrentEmojis: levelConfig.maxConcurrentEmojis
    };
  }

  static async initializeLevel(level: number, windowSize: WindowSize): Promise<{
    paths: Point[][];
    config: EmojiGameConfig;
  }> {
    const levelConfig = this.getLevelConfig(level);
    const paths = await this.generateLevelPaths(levelConfig, windowSize);
    const config = this.calculateLevelConfig(levelConfig);
    return { paths, config };
  }

  static async initializeEmojiLevel(level: number, windowSize: WindowSize): Promise<{
    paths: Point[][];
    config: EmojiGameConfig;
    levelConfig: EmojiGameLevel;
  }> {
    const levelConfig = this.getEmojiLevelConfig(level);
    const paths = await this.generateEmojiLevelPaths(levelConfig, windowSize);
    const config = this.calculateEmojiLevelConfig(levelConfig);
    return { paths, config, levelConfig };
  }

  static isEmojiLevelComplete(emojis: CapturedEmoji[]): boolean {
    return emojis.every(emoji => emoji.status === 'rescued');
  }

  static getNextLevel(currentLevel: number): number {
    return currentLevel + 1;
  }

  static hasMoreLevels(currentLevel: number): boolean {
    // With dynamic level generation, the game can continue indefinitely until the player loses.
    // Therefore, there are always 'more levels' as long as the player keeps winning.
    return true;
  }

  static getThemeForLevel(level: number): EmojiTheme {
    return getThemeForLevel(level);
  }

  static isNewThemeUnlocked(level: number): boolean {
    const currentTheme = getThemeForLevel(level);
    const previousTheme = getThemeForLevel(level - 1);
    return currentTheme.name !== previousTheme.name;
  }

  static getThemeUnlockMessage(level: number): string | null {
    if (this.isNewThemeUnlocked(level)) {
      const theme = getThemeForLevel(level);
      return `�� New Theme Unlocked: ${theme.name}! ${theme.description}`;
    }
    return null;
  }

  static async initializeLevelWithEmojis(level: number, windowSize: WindowSize): Promise<{
    paths: Point[][];
    config: EmojiGameConfig;
  }> {
    try {
      const levelConfig = this.getLevelConfig(level);
      
      // Generate multiple paths based on emoji count for this level
      const paths: Point[][] = [];
      for (let i = 0; i < levelConfig.emojiCount; i++) {
        const path = await gameService.generatePath(windowSize);
        paths.push(path);
      }
      
      const config = this.calculateEmojiLevelConfig(levelConfig);

      return { paths, config };
    } catch (error) {
      console.error('Error initializing emoji level:', error);
      throw error;
    }
  }
}

export const levelService = new LevelService(); 